/**
 * Perímetro del recinto: el polígono amarillo que define qué superficie hay que
 * cardioproteger.
 *
 * Se traza haciendo clic en el mapa y se cierra como en Google Earth: clic cerca
 * del primer vértice, o el botón. Los vértices quedan arrastrables también
 * después de cerrar, porque ajustar el borde al límite real del terreno es un
 * trabajo iterativo.
 */

import L from 'leaflet';
import { polygonAreaM2 } from '../domain/geo.js';
import { vertexIcon } from './icons.js';

/** Radio en píxeles alrededor del primer vértice que cierra el polígono. */
const CLOSE_SNAP_PX = 14;

export class PerimeterLayer {
  constructor(map, { onChange, onCommit }) {
    this.map = map;
    this.onChange = onChange;
    this.onCommit = onCommit;
    this.points = [];
    this.polygon = null;
    this.vertexMarkers = [];
    this.drawing = false;

    this.map.on('click', (event) => this.#onMapClick(event));
    document.addEventListener('keydown', (event) => this.#onKeyDown(event));
  }

  get areaM2() {
    return polygonAreaM2(this.points);
  }

  /** ¿Hay un polígono utilizable para calcular cobertura? */
  get isClosed() {
    return Boolean(this.polygon) && this.points.length >= 3;
  }

  /** Alterna entre empezar a trazar y cerrar el trazado. Devuelve si quedó dibujando. */
  toggleDrawing() {
    if (this.drawing) {
      this.finish();
    } else {
      this.drawing = true;
      if (this.polygon) {
        this.map.removeLayer(this.polygon);
        this.polygon = null;
      }
      this.points = [];
      this.map.getContainer().style.cursor = 'crosshair';
      this.#refreshVertices();
      this.onChange();
    }
    return this.drawing;
  }

  finish() {
    this.drawing = false;
    this.map.getContainer().style.cursor = '';
    this.#refreshVertices();
    this.onChange();
    this.onCommit();
  }

  #onMapClick(event) {
    if (!this.drawing) return;

    // Cierre por proximidad al primer vértice.
    if (this.points.length >= 3) {
      const first = this.map.latLngToContainerPoint(L.latLng(this.points[0][0], this.points[0][1]));
      const clicked = this.map.latLngToContainerPoint(event.latlng);
      if (Math.hypot(clicked.x - first.x, clicked.y - first.y) < CLOSE_SNAP_PX) {
        this.finish();
        return;
      }
    }

    this.points.push([event.latlng.lat, event.latlng.lng]);
    this.#syncPolygon();
    this.#refreshVertices();
    this.onChange();
  }

  #onKeyDown(event) {
    if (!this.drawing) return;
    const isUndo =
      event.key === 'Backspace' ||
      ((event.ctrlKey || event.metaKey) && (event.key === 'z' || event.key === 'Z'));
    if (isUndo) {
      event.preventDefault();
      this.undoVertex();
    }
  }

  undoVertex() {
    if (!this.points.length) return;
    this.points.pop();
    this.#syncPolygon();
    this.#refreshVertices();
    this.onChange();
  }

  #syncPolygon() {
    if (!this.points.length) {
      if (this.polygon) {
        this.map.removeLayer(this.polygon);
        this.polygon = null;
      }
      return;
    }
    if (!this.polygon) {
      this.polygon = L.polygon(this.points, {
        color: '#ffdd00',
        weight: 3,
        fillOpacity: 0.05,
      }).addTo(this.map);
    } else {
      this.polygon.setLatLngs(this.points);
    }
  }

  #refreshVertices() {
    this.vertexMarkers.forEach((marker) => this.map.removeLayer(marker));
    this.vertexMarkers = [];

    const shouldShow = this.drawing || this.isClosed;
    if (!shouldShow) return;

    this.points.forEach((point, index) => {
      const isFirst = index === 0;
      const marker = L.marker([point[0], point[1]], {
        draggable: true,
        zIndexOffset: 1000,
        icon: vertexIcon(isFirst),
      }).addTo(this.map);

      marker.on('drag', (event) => {
        const position = event.target.getLatLng();
        this.points[index] = [position.lat, position.lng];
        if (this.polygon) this.polygon.setLatLngs(this.points);
        this.onChange();
      });
      marker.on('dragend', () => this.onCommit());

      if (this.drawing && isFirst && this.points.length >= 3) {
        marker.bindTooltip('Cerrar perímetro', { direction: 'top' });
        marker.on('click', (event) => {
          L.DomEvent.stopPropagation(event);
          this.finish();
        });
      }

      this.vertexMarkers.push(marker);
    });
  }

  /** Carga un perímetro guardado, ya cerrado. */
  load(points) {
    this.clear();
    if (!points || !points.length) return;
    this.points = points.map((point) => [point[0], point[1]]);
    this.#syncPolygon();
    this.#refreshVertices();
    this.onChange();
  }

  clear() {
    if (this.polygon) {
      this.map.removeLayer(this.polygon);
      this.polygon = null;
    }
    this.points = [];
    this.vertexMarkers.forEach((marker) => this.map.removeLayer(marker));
    this.vertexMarkers = [];
    if (this.drawing) this.finish();
    else this.onChange();
  }
}
